<template>
    <Modal v-model="isOpen" @view:willPresent="onModalOpen" title="Ersatz suchen / Storno">
        <template v-if="withdrawalLoading">
            <Skeleton :items="['title','line','line','margin','button','button']" />
        </template>

        <!-- WR exists -->
        <div v-else-if="withdrawalRequest" class="local-text">
            <p class="globalJustifyHyphenate"><Ines/> versucht, einen Ersatz für dich zu finden.
                Der Dienst verbleibt solange in deinem Dienstplan.
            </p>
            <br>

            <!-- WR pending -->
            <!-- prediction wording -->
            <div v-if="isPending(withdrawalRequest)">
                <h4 class="globalHeader">Restzeit</h4>
                <Ines/> versucht noch bis <span class="value">{{ timeToDeadlineString }}</span> Ersatz zu finden.<br><br>
                <h4 class="globalHeader">Rückmeldungen</h4>

                Es wurde noch kein Ersatz für diesen Dienst gefunden.<br/>
                <template  v-if="!stats || stats.countSuggestees === null || stats.countSuggestees === undefined || stats.countSeen === null || stats.countSeen === undefined "></template>
                <template v-else-if="stats.countSuggestees === 0">
<!--                    Es gibt niemanden, dem wir den Dienst innerhalb der IBFG vorschlagen können.-->
                    Aktuell gibt es auch keine freien Ersatzmitarbeiter, denen dieser Dienst angeboten werden kann.
                </template>
                <template v-else-if="stats.countSuggestees - stats.countSeen === 0">
                    Alle möglichen Ersatzmitarbeiter haben diese Diensttauschanfrage bereits gesehen.
                </template>
                <template v-else>
                    <span class="value">{{ ucfirst(pluralizeHuman(stats.countSuggestees - stats.countSeen, "einer", "")) }}</span>
                    von <span class="value">{{pluralizeHuman(stats?.countSuggestees, "einem", "", "" ) }}</span>
                    möglichen Ersatzmitarbeitern haben diese Diensttauschanfrage noch nicht gesehen.
                </template>

            </div>

            <!-- WR finished -->
            <div v-else-if="isFinished(withdrawalRequest)">
                Die Suche nach Ersatz ist beendet. {{ getStatusStringFor(withdrawalRequest) }}
            </div>

        </div>

        <!-- WR to be created -->
        <div v-else-if="!withdrawalRequest" class="local-text">

            <!-- shortcut: instant withdraw possible -->
            <template v-if="cancelStep===1 && instantPossible">
                <div>
                    <p class="globalJustifyHyphenate">Ein Kollege ist als passender Ersatz verfügbar und kann den Dienst direkt übernehmen.</p>
                </div>
                <p class="globalJustifyHyphenate">
                    Dein Dienst wird sofort umgebucht und aus deinem Dienstplan entfernt. Bitte informiere die Station über den Mitarbeiterwechsel.
                </p>
                <br>
                <RichResponseListItem
                    :title="getLabelForShift(booking.shiftType) + ': ' + booking?.jobsite.abbreviation"
                    :icon="booking.date"
                    icon-type="date"
                    :image="getImageForJobsite(booking.jobsite)"
                >
                    <Contact display-mode="standalone" v-if="booking?.shiftInformation.requestedBy" :contact="booking?.shiftInformation.requestedBy" />
                    <Contact display-mode="standalone" v-else-if="booking?.jobsite.contact" :contact="booking?.jobsite.contact" />
                </RichResponseListItem>
            </template>

            <!-- no shortcut: select keep / cancel mode -->
            <template v-else-if="cancelStep===1 && !instantPossible">
                <p class="globalJustifyHyphenate">Wähle die Dringlichkeit deiner Abgabe</p><br>
                <RichResponseListItem
                    selectable
                    :selected="withdrawalMode===BookingWithdrawalRequestUnclaimedConsequence.Keep"
                    :icon-type="null"
                    @click="withdrawalMode=BookingWithdrawalRequestUnclaimedConsequence.Keep; cancelStep=1"
                >
                    <template #title>Ersatz suchen</template>
                    <template #default>
                        <p class="muted">Der Dienst verbleibt in deinem Dienstplan. ines versucht, einen Ersatz für dich zu finden. Während dieser Zeit bleibst du über den Vorgang informiert.</p>
                    </template>
                </RichResponseListItem>
                <RichResponseListItem
                    selectable
                    :selected="withdrawalMode===BookingWithdrawalRequestUnclaimedConsequence.Cancel"
                    :icon-type="null"
                    @click="withdrawalMode=BookingWithdrawalRequestUnclaimedConsequence.Cancel; cancelStep=1"
                >
                    <template #title>Dienst stornieren</template>
                    <template #default>
                        <p class="muted">Der Dienst wird sofort aus deinem Dienstplan entfernt. ines versucht noch für eine kurze Zeit, einen Ersatz zu finden und storniert den Dienst im Anschluss für dich.</p>
                    </template>
                </RichResponseListItem>
                <br><br>
                <ion-textarea
                    v-if="!instantPossible"
                    v-model="withdrawalMessageInputModel"
                    label="Nachricht an deine Kollegen"
                    label-placement="stacked"
                    placeholder="Warum suchst du Ersatz?"
                ></ion-textarea>

                <div v-if="withdrawalMode===BookingWithdrawalRequestUnclaimedConsequence.Keep">
                    <br>
                    <p v-if="stats?.countSuggestees" class="globalJustifyHyphenate">Chance auf Ersatz: wir fragen <span class="value">{{ pluralizeHuman(stats?.countSuggestees ?? 0, 'eine Kollegin', 'Kolleginnen', 'keine Kolleginnen') }}</span><span v-if="stats?.countSeen !== null">; bereits gesehen: <span class="value">{{ stats?.countSeen ?? 0 }}</span></span>.</p>
                    <p v-else-if="!instantPossible" class="globalJustifyHyphenate">Keine passende Verfügbarkeit anderer Mitglieder. Wir suchen bis <span class="value">{{ deadline }}</span> nach Ersatz, falls wir niemanden finden, bleibt der Dienst bei dir.</p>
                </div>
                <TextWithIcon
                    v-else-if="cancelWarningRenderSet"
                    :small="true"
                    :warning="cancelWarningRenderSet.color==='warning'"
                    :error="cancelWarningRenderSet.color==='error'"
                >
                    <template #icons><InlineSVG :src="KapitelIcon.warning"/></template>
                    <p>{{ cancelWarningRenderSet.text }}</p>
                </TextWithIcon>
            </template>

            <!-- no shortcut, cancel mode, second step -->
            <div v-else-if="cancelStep===2">
                <p class="globalJustifyHyphenate">Bitte informiere die Station darüber, dass du den Dienst nicht antreten kannst. Weise darauf hin, dass ines den Dienst am <span class="value">{{ deadline }}</span> storniert sollte sie keinen Ersatz finden.</p>
                <br><br>
                <RichResponseListItem
                    :title="getLabelForShift(booking.shiftType) + ': ' + booking?.jobsite.abbreviation"
                    :icon="booking.date"
                    icon-type="date"
                    :image="getImageForJobsite(booking.jobsite)"
                >
                    <div>
                        <Contact display-mode="standalone" v-if="booking?.shiftInformation.requestedBy" :contact="booking?.shiftInformation.requestedBy" />
                        <Contact display-mode="standalone" v-else-if="booking?.jobsite.contact" :contact="booking?.jobsite.contact" />
                        <br>
                        <p>Storno {{ deadline }}</p>
                    </div>
                </RichResponseListItem>
                <br>
                <ion-item lines="none">
                    <ion-checkbox slot="start"
                                  v-model="confirmInformStationModel"
                                  aria-label="Station informieren"
                                  labelPlacement="end">
                        Ich informiere die Station.
                    </ion-checkbox>


                </ion-item>
            </div>
        </div>


        <template #actions="{dismiss}">
            <template v-if="withdrawalLoading">
                <Skeleton :items="['button','button']" />
            </template>
            <template v-else>
                <ion-button v-if="withdrawalRequest && !isFinished(withdrawalRequest)" color="primary" expand="block" @click="removeWithdrawal">
                    Suche abbrechen
                </ion-button>
                <template v-else>
                    <ion-button v-if="withdrawalMode===BookingWithdrawalRequestUnclaimedConsequence.Keep" color="primary" expand="block" @click="createWithdrawalKeep">
                        {{ instantPossible ? "Direkt umbuchen" : "Ersatz suchen" }}
                    </ion-button>
                    <template v-else>
                        <ion-button v-if="cancelStep===1" color="primary" expand="block" @click="cancelStep=2">
                            Weiter
                        </ion-button>
                        <div v-else class="cancel-actions">
                            <ion-button color="primary" expand="block" :disabled="!confirmInformStation" @click="createWithdrawalCancel">
                                Dienst stornieren
                            </ion-button>
                        </div>
                    </template>
                </template>
                <ion-button
                    v-if="!(withdrawalMode===BookingWithdrawalRequestUnclaimedConsequence.Cancel && cancelStep===2 && !withdrawalRequest)"
                    color="light"
                    expand="block"
                    @click="dismiss"
                >
                    Schließen
                </ion-button>
                <ion-button
                    v-else
                    color="light"
                    expand="block"
                    @click="cancelStep=1"
                >
                    Zurück
                </ion-button>
            </template>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import {
    IonButton,
    IonCheckbox,
    IonItem,
    IonLabel,
    IonTextarea
} from "@ionic/vue";
import {PropType, computed, ref, watch} from "vue";
import Modal from "@/components/Modal.vue";
import {
    Booking,
    BookingWithdrawalPotentialSuccessStats,
    BookingWithdrawalRequest,
    BookingWithdrawalRequestStatus,
    BookingWithdrawalRequestUnclaimedConsequence
} from "@/graphql/generated/graphql";
import RichResponseListItem from "@/views/Chat/RichResponses/components/RichResponseListItem.vue";
import Skeleton from "@/components/Skeleton.vue";
import {floatFormat, pluralizeHuman, pluralizeUnitOnly, ucfirst} from "@/helper/amountFormatter";
import Contact from "@/views/CalendarDetail/components/Contact.vue";
import {getLabelForShift} from "@/helper/shiftTypes";
import {getImageForJobsite} from "@/helper/jobsite";
import {
    computeDeadlineForRequestFinish,
    createBookingWithdrawalRequest,
    fetchBookingWithdrawalPotentialSuccessStats,
    fetchWithdrawalRequests, getStatusStringFor, isFinished, isPending,
    removeBookingWithdrawalRequest,
    setRequestWithdrawalToSeenByWithdrawer,
    useBookingWithdrawalDraftStore
} from "@/helper/bookingWithdrawalRequest";
import datetime from "@/helper/datetime/datetime";
import {useGlobalEmitter} from "@/helper/emitter";
import Ines from "@/views/components/Ines.vue";
import InlineSVG from "@/components/InlineSVG.vue";
import TextWithIcon from "@/components/TextWithIcon.vue";
import {KapitelIcon} from "@/helper/icon";

const props = defineProps({
    booking: {
        type: Object as PropType<Booking>,
        required: true
    }
})

const emit = defineEmits<{
    'update:isOpen': [value: boolean],
    changed: []
}>()

const isOpen = defineModel('isOpen', {default: false, type: Boolean})

const withdrawalRequest = ref<BookingWithdrawalRequest | null>(null)
const instantPossible = computed(() => stats.value?.instantWithdrawalPossible)
const withdrawalLoading = ref(false)
const withdrawalMode = ref<BookingWithdrawalRequestUnclaimedConsequence>(BookingWithdrawalRequestUnclaimedConsequence.Keep)
const cancelStep = ref<1|2>(1)
const confirmInformStation = ref(false)
const withdrawalMessageInput = ref<string | undefined>(undefined)
const withdrawalDraftStore = useBookingWithdrawalDraftStore()

const withdrawalMessageInputModel = computed({
    get: () => withdrawalMessageInput.value,
    set: (val) => {
        withdrawalMessageInput.value = val
        withdrawalDraftStore.setDraft(val, withdrawalMode.value)
    }
})

const confirmInformStationModel = computed({
    get: () => confirmInformStation.value,
    set: (val) => confirmInformStation.value = val
})

const cancelWarningRenderSet = computed(() => {
    if (withdrawalMode.value === BookingWithdrawalRequestUnclaimedConsequence.Cancel && props.booking) {

        const hoursUntilBooking = datetime.getDateTimeDiffInHours(datetime.getNow(), props.booking.begin)
        if (hoursUntilBooking <= 24) {
            return {
                color: 'error',
                text: `Der Dienst beginnt in ${floatFormat(hoursUntilBooking, 0)} Stunden. Eine solch kurzfristige Stornierung erlaubt dem Entleiher Strafzahlungen in Rechnung zu stellen und verursacht gegebenenfalls hohe Kosten für die IBFG.`
            }
        } else if (hoursUntilBooking <= (2400 * 7)) {
            return {
                color: 'warning',
                text: `Der Dienst beginnt in ${floatFormat(hoursUntilBooking/24, 0)} Tagen. Bitte beachte, dass kurzfristigen Stornierungen häufig negative Konsequenzen für die Station bedeuten und es der Station vielfach nicht gelingt, einen Ersatzmitarbeiter zu finden.`
            }
        }
    }

    return undefined
})

const stats = ref(undefined as undefined | BookingWithdrawalPotentialSuccessStats)

const deadline = computed(() => {
    const dl = computeDeadlineForRequestFinish(props.booking, withdrawalMode.value)
    return datetime.formatKapitelDateTime(dl, 'dd.LL. HH:mm')
})

const timeToDeadlineString = computed(() => {
    const dl = withdrawalRequest.value?.deadline
    if (!dl) return ''
    return datetime.formatKapitelDateTime(dl, 'dd.LL. HH:mm')
})

const loadWithdrawalData = async () => {
    withdrawalLoading.value = true
    try {
        const requests = await fetchWithdrawalRequests()
        withdrawalRequest.value = requests.find(r => r.booking.id === props.booking.id) ?? null
        stats.value = await fetchBookingWithdrawalPotentialSuccessStats(props.booking.id as number)
    } finally {
        withdrawalLoading.value = false
    }
}

const createWithdrawalKeep = async () => {
    if (withdrawalLoading.value) return
    withdrawalLoading.value = true
    try {
        await createBookingWithdrawalRequest(
            props.booking,
            BookingWithdrawalRequestUnclaimedConsequence.Keep,
            withdrawalMessageInput.value ?? null
        )
        await loadWithdrawalData()
        emit('changed')
        isOpen.value = false
    } finally {
        withdrawalLoading.value = false
    }
}

const createWithdrawalCancel = async () => {
    if (withdrawalLoading.value) return
    withdrawalLoading.value = true
    isOpen.value = false
    try {
        await createBookingWithdrawalRequest(
            props.booking,
            BookingWithdrawalRequestUnclaimedConsequence.Cancel,
            withdrawalMessageInput.value ?? null
        )
        await loadWithdrawalData()
        emit('changed')
    } finally {
        withdrawalLoading.value = false
    }
}

const removeWithdrawal = async () => {
    if (!withdrawalRequest.value || withdrawalLoading.value) return
    withdrawalLoading.value = true
    try {
        await removeBookingWithdrawalRequest(props.booking)
        withdrawalRequest.value = null
        emit('changed')
        isOpen.value = false
    } finally {
        withdrawalLoading.value = false
    }
}

const onModalOpen = async () => {
    withdrawalMode.value = instantPossible.value ? BookingWithdrawalRequestUnclaimedConsequence.Keep : (withdrawalDraftStore.lastMode ?? BookingWithdrawalRequestUnclaimedConsequence.Keep)
    cancelStep.value = 1
    confirmInformStation.value = false
    withdrawalMessageInput.value = withdrawalDraftStore.lastMessage ?? undefined
    await loadWithdrawalData()
    if (withdrawalRequest.value && isFinished(withdrawalRequest.value)) {
        await setRequestWithdrawalToSeenByWithdrawer(withdrawalRequest.value.id as number)
    }
}

watch(withdrawalMode, (mode) => withdrawalDraftStore.setDraft(withdrawalMessageInput.value, mode))
</script>

<style scoped>
.local-text{
    color: var(--ion-color-grey);
    .globalHeader{
        color: #000;
    }
}
span.value {
    font-weight: bold;
}
</style>
